// Per-integration permission helpers.
//
// The team-permissions dialog stores granted sidebar groups in a flat string
// array on `users.preferences.menu_permissions`. Per-integration permissions
// piggy-back on the same array using the prefix `integration:<key>` (e.g.
// `integration:payu`) — this keeps the storage shape unchanged so no
// migration / new column is needed.
//
// Resolution rules (in order):
//   1. menu_permissions === null/undefined → full access (legacy default).
//   2. parent `settings` group not granted → no integration access at all.
//   3. menu_permissions has NO `integration:*` keys → all integrations
//      allowed (legacy mode — preserves existing custom-permission users
//      who haven't been migrated to the per-integration model yet).
//   4. menu_permissions has at least one `integration:*` key → only the
//      explicitly listed integrations are allowed.

export const INTEGRATION_PERMISSION_PREFIX = 'integration:';

export const integrationPermissionId = (entitlementKey: string): string =>
  `${INTEGRATION_PERMISSION_PREFIX}${entitlementKey}`;

export const isIntegrationPermissionId = (id: string): boolean =>
  id.startsWith(INTEGRATION_PERMISSION_PREFIX);

export const entitlementKeyFromPermissionId = (id: string): string | null =>
  isIntegrationPermissionId(id) ? id.slice(INTEGRATION_PERMISSION_PREFIX.length) : null;

export interface IntegrationPermissionInput {
  menu_permissions: string[] | null | undefined;
  entitlementKey: string;
}

export const isIntegrationGrantedByPermissions = ({
  menu_permissions,
  entitlementKey,
}: IntegrationPermissionInput): boolean => {
  if (!Array.isArray(menu_permissions)) return true;
  if (!menu_permissions.includes('settings')) return false;
  const hasExplicitIntegrationKeys = menu_permissions.some(isIntegrationPermissionId);
  if (!hasExplicitIntegrationKeys) return true;
  return menu_permissions.includes(integrationPermissionId(entitlementKey));
};
